import React from 'react';
import { motion } from 'framer-motion';
import { ZapIcon, SettingsIcon, LayersIcon, ArrowRightIcon, ArrowLeftIcon } from 'lucide-react';
import { SlideLayout, itemVariants } from '../ui/SlideLayout';
export function FPGAAdvantageSlide() {
  return <SlideLayout data-id="element-106">
      <motion.div variants={itemVariants} className="mb-16 text-center" data-id="element-107">
        <h2 className="text-4xl md:text-5xl font-bold text-brand-text mb-4" data-id="element-108">
          FPGAs: The Sweet Spot
        </h2>
        <p className="text-xl text-brand-text-dim" data-id="element-109">Between ASICs and GPUs</p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16" data-id="element-110">
        {/* ASIC */}
        <motion.div variants={itemVariants} className="bg-brand-bg-card/50 border border-brand-border rounded-2xl p-8 flex flex-col" data-id="element-111">
          <ZapIcon className="w-10 h-10 text-brand-text-muted mb-6" data-id="element-112" />
          <h3 className="text-2xl font-bold text-brand-text mb-4" data-id="element-113">ASIC</h3>
          <p className="text-brand-text-muted text-sm mb-6 font-mono" data-id="element-114">
            Google TPU, Groq LPU
          </p>
          <ul className="space-y-4 text-brand-text-dim flex-1" data-id="element-115">
            <li className="flex items-start gap-2" data-id="element-116">
              <span className="text-green-500 mt-1" data-id="element-117">•</span> Blazing fast
              performance
            </li>
            <li className="flex items-start gap-2" data-id="element-118">
              <span className="text-red-500 mt-1" data-id="element-119">•</span> Circuits frozen at
              fabrication
            </li>
            <li className="flex items-start gap-2" data-id="element-120">
              <span className="text-red-500 mt-1" data-id="element-121">•</span> Millions of dollars
              to reprogram
            </li>
            <li className="flex items-start gap-2" data-id="element-122">
              <span className="text-red-500 mt-1" data-id="element-123">•</span> Months of lead time
            </li>
          </ul>
        </motion.div>

        {/* FPGA */}
        <motion.div variants={itemVariants} className="bg-brand-bg-card border-2 border-brand-teal rounded-2xl p-8 flex flex-col relative shadow-[0_0_30px_rgba(0,212,170,0.15)] transform md:-translate-y-4" data-id="element-124">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-1 bg-brand-teal rounded-b-full shadow-[0_0_10px_rgba(0,212,170,0.5)]" data-id="element-125" />
          <SettingsIcon className="w-10 h-10 text-brand-teal mb-6" data-id="element-126" />
          <h3 className="text-2xl font-bold text-brand-text mb-4" data-id="element-127">FPGA</h3>
          <p className="text-brand-teal text-sm mb-6 font-mono" data-id="element-128">OpenFPGA</p>
          <ul className="space-y-4 text-brand-text flex-1" data-id="element-129">
            <li className="flex items-start gap-2" data-id="element-130">
              <span className="text-brand-teal mt-1" data-id="element-131">•</span> ~90% of ASIC
              performance
            </li>
            <li className="flex items-start gap-2" data-id="element-132">
              <span className="text-brand-teal mt-1" data-id="element-133">•</span> Fully
              reprogrammable hardware
            </li>
            <li className="flex items-start gap-2" data-id="element-134">
              <span className="text-brand-teal mt-1" data-id="element-135">•</span> Gates reconfigure
              to the workload
            </li>
            <li className="flex items-start gap-2" data-id="element-136">
              <span className="text-brand-teal mt-1" data-id="element-137">•</span> No silicon respin,
              zero NRE
            </li>
          </ul>
        </motion.div>

        {/* GPU */}
        <motion.div variants={itemVariants} className="bg-brand-bg-card/50 border border-brand-border rounded-2xl p-8 flex flex-col" data-id="element-138">
          <LayersIcon className="w-10 h-10 text-brand-text-muted mb-6" data-id="element-139" />
          <h3 className="text-2xl font-bold text-brand-text mb-4" data-id="element-140">GPU</h3>
          <p className="text-brand-text-muted text-sm mb-6 font-mono" data-id="element-141">
            NVIDIA H100, A100
          </p>
          <ul className="space-y-4 text-brand-text-dim flex-1" data-id="element-142">
            <li className="flex items-start gap-2" data-id="element-143">
              <span className="text-green-500 mt-1" data-id="element-144">•</span> Flexible &
              general-purpose
            </li>
            <li className="flex items-start gap-2" data-id="element-145">
              <span className="text-red-500 mt-1" data-id="element-146">•</span> Wasted silicon on
              unused features
            </li>
            <li className="flex items-start gap-2" data-id="element-147">
              <span className="text-red-500 mt-1" data-id="element-148">•</span> Non-deterministic
              scheduling
            </li>
            <li className="flex items-start gap-2" data-id="element-149">
              <span className="text-red-500 mt-1" data-id="element-150">•</span> High power draw
            </li>
          </ul>
        </motion.div>
      </div>

      {/* Spectrum Bar */}
      <motion.div variants={itemVariants} className="max-w-4xl mx-auto" data-id="element-151">
        <div className="flex items-center justify-between text-sm font-mono text-brand-text-muted mb-2 px-4" data-id="element-152">
          <span className="flex items-center gap-2" data-id="element-153">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-154" /> Speed
          </span>
          <span data-id="element-155">
            Flexibility <ArrowRightIcon className="w-4 h-4 inline" data-id="element-156" />
          </span>
        </div>
        <div className="h-2 bg-brand-border rounded-full relative overflow-hidden flex" data-id="element-157">
          <div className="flex-1 bg-gradient-to-r from-brand-text-muted to-brand-teal/50" data-id="element-158" />
          <div className="flex-1 bg-brand-teal" data-id="element-159" />
          <div className="flex-1 bg-gradient-to-r from-brand-teal/50 to-brand-text-muted" data-id="element-160" />
        </div>
        <div className="flex justify-between text-xs font-bold text-brand-text mt-4 px-8" data-id="element-161">
          <span className="w-1/3 text-left" data-id="element-162">ASIC</span>
          <span className="w-1/3 text-center text-brand-teal text-base" data-id="element-163">
            FPGA
          </span>
          <span className="w-1/3 text-right" data-id="element-164">GPU</span>
        </div>
      </motion.div>
    </SlideLayout>;
}